import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const services = [
  {
    icon: 'ri-search-eye-line',
    tag: 'Audit',
    title: 'AI Visibility Audit',
    desc: 'We start by testing your current AI visibility across ChatGPT, Google Gemini, Perplexity, and others. We run relevant queries your customers would actually ask, document where you appear (and where you don\'t), and benchmark you against your top three competitors.',
  },
  {
    icon: 'ri-article-line',
    tag: 'Content',
    title: 'Content Restructuring for AI',
    desc: 'We audit your existing website content and restructure it for AI retrieval — adding clear headings, direct Q&A formatting, statistics, citations, and the structural signals that make AI systems want to cite you.',
  },
  {
    icon: 'ri-quill-pen-line',
    tag: 'Content',
    title: 'AI-Optimised Content Creation',
    desc: 'We create new content specifically built for AI discovery — FAQ pages, service explainers, local authority content, and expert-driven articles that answer the questions your customers are asking AI right now.',
  },
  {
    icon: 'ri-shield-star-line',
    tag: 'Authority',
    title: 'Authority Signal Building',
    desc: 'AI systems assess authority by looking at what the broader internet says about you. We build that through structured review generation, third-party directory consistency, local PR and mentions, and schema markup that makes your business easy for AI to understand.',
  },
  {
    icon: 'ri-code-s-slash-line',
    tag: 'Technical',
    title: 'AI Crawlability & Technical Setup',
    desc: 'Many websites accidentally block AI crawlers without knowing it. We audit and fix your technical setup so that ChatGPT, Perplexity, and other AI systems can actually access and index your content.',
  },
  {
    icon: 'ri-bar-chart-2-line',
    tag: 'Reporting',
    title: 'Monthly AI Visibility Reporting',
    desc: 'Every month, you receive a report showing exactly where your business appears in AI-generated answers across major platforms — compared to your competitors. Clear metrics: citation rate, share of voice, brand mention frequency, and citation velocity.',
  },
  {
    icon: 'ri-refresh-line',
    tag: 'Ongoing',
    title: 'Ongoing Optimisation',
    desc: 'AI systems update constantly. What gets cited today may be outcompeted tomorrow. We monitor changes, adapt your content strategy, and keep you ahead of competitors who are starting to pay attention.',
  },
];

const tagColors: Record<string, string> = {
  Audit: 'border-ds-red/40 text-ds-red bg-ds-red/8',
  Content: 'border-white/20 text-white/50',
  Authority: 'border-white/20 text-white/50',
  Technical: 'border-white/20 text-white/50',
  Reporting: 'border-white/20 text-white/50',
  Ongoing: 'border-white/20 text-white/50',
};

export default function WhatIncludedSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="whats-included" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">The Service</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none reveal">
              EVERYTHING YOU NEED
              <br />
              TO BECOME THE BUSINESS
              <br />
              <span className="text-ds-red neon-text-red">AI RECOMMENDS.</span>
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs reveal">
              Our GEO / AEO service is a comprehensive, ongoing programme built around making your business visible in AI-generated answers — across every platform your customers are using.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Main 3-col grid (first 6) */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ds-border/30 mb-px reveal">
          {services.slice(0, 6).map((item) => (
            <div key={item.title} className="bg-ds-bg p-8 group hover:bg-ds-bg-2 transition-colors duration-300">
              <span className={`inline-block border px-2 py-0.5 font-body text-xs tracking-widest uppercase mb-5 ${tagColors[item.tag] ?? 'border-ds-border text-white/40'}`}>
                {item.tag}
              </span>
              <div className="w-10 h-10 flex items-center justify-center border border-ds-border group-hover:border-ds-red/50 transition-colors duration-300 mb-4">
                <i className={`${item.icon} text-white/40 group-hover:text-ds-red text-lg transition-colors duration-300`} />
              </div>
              <h3 className="font-display text-white text-base tracking-wide leading-tight mb-3">{item.title}</h3>
              <div className="h-px bg-ds-border mb-3 group-hover:bg-ds-red/20 transition-colors duration-300" />
              <p className="font-body text-white/55 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* 7th item — full width */}
        {services.slice(6).map((item) => (
          <div key={item.title} className="bg-ds-bg border border-ds-border p-8 flex flex-col md:flex-row gap-6 group hover:border-ds-red/30 transition-colors duration-300 reveal">
            <div className="flex items-start gap-4 md:w-64 flex-shrink-0">
              <div className="w-10 h-10 flex items-center justify-center border border-ds-border group-hover:border-ds-red/50 transition-colors duration-300 flex-shrink-0">
                <i className={`${item.icon} text-white/40 group-hover:text-ds-red text-lg transition-colors duration-300`} />
              </div>
              <div>
                <span className={`inline-block border px-2 py-0.5 font-body text-xs tracking-widest uppercase mb-1 ${tagColors[item.tag] ?? 'border-ds-border text-white/40'}`}>
                  {item.tag}
                </span>
                <h3 className="font-display text-white text-base tracking-wide leading-tight">{item.title}</h3>
              </div>
            </div>
            <div className="hidden md:block w-px bg-ds-border flex-shrink-0" />
            <p className="font-body text-white/55 text-sm leading-relaxed flex-1">{item.desc}</p>
          </div>
        ))}

        {/* Bottom note */}
        <div className="mt-8 border border-ds-border bg-ds-bg p-6 flex items-center gap-4 reveal">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
            <i className="ri-information-line text-ds-red text-lg" />
          </div>
          <p className="font-body text-white/50 text-sm leading-relaxed">
            Every GEO engagement starts with the <strong className="text-white">AI Visibility Audit</strong> — a complete map of your current AI presence. From there, we build a tailored programme based on your specific gaps, market, and goals.
          </p>
        </div>

      </div>
    </section>
  );
}
